import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import RegisterProviderDialog from '../../components/onboard/RegisterProviderDialog';
import OnboardDocumentsDialog from '../../components/onboard/OnboardDocumentsDialog';
import OnboardDriversDialog from '../../components/onboard/OnboardDriversDialog';
import OnboardVehiclesDialog from '../../components/onboard/OnboardVehiclesDialog';
import { useAppSelector } from '../../hooks/useAppSelector';
import * as providerSlice from '../../data/provider.slice';
import * as userSlice from '../../data/user.slice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import * as referenceSlice from '../../data/reference.slice';
import { DateTime } from 'luxon';

const PREFIX = 'OnboardHome';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  button: `${PREFIX}-button`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    height: '100%',
    flexDirection: 'row',
    flex: '0 1 100%',
    backgroundColor: theme.palette.background.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.content}`]: {
    marginTop: theme.spacing(4),
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  [`& .${classes.button}`]: {
    marginLeft: theme.spacing(4),
  },
}));

export default function OnboardHome() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const userInfo = useAppSelector(userSlice.selectUserInfo);
  const uploadingDocument = useAppSelector(
    providerSlice.selectUploadingDocument,
  );
  const documentTypes = useAppSelector(
    referenceSlice.selectProviderDocumentTypes,
  );
  const documents = useAppSelector(providerSlice.selectProviderDocuments);

  const [companyInfoOpen, setCompanyInfoOpen] = useState(false);
  const [uploadDocumentsOpen, setUploadDocumentsOpen] = useState(false);
  const [onboardDriversOpen, setOnboardDriversOpen] = useState(false);
  const [onboardVehiclesOpen, setOnboardVehiclesOpen] = useState(false);
  const [canCloseRegister, setCanCloseRegister] = useState(false);
  const [documentCount, setDocumentCount] = useState<number>(0);

  useEffect(() => {
    dispatch(userSlice.fetchUserInfoAsync());
  }, [companyInfoOpen]);

  useEffect(() => {
    if (userInfo?.transportationProvider?.transportationProviderID) {
      setCanCloseRegister(true);

      dispatch(
        providerSlice.getProviderAsync(
          userInfo?.transportationProvider?.transportationProviderID,
        ),
      );
    } else {
      setCanCloseRegister(false);
      setCompanyInfoOpen(true);
    }
  }, [userInfo]);

  useEffect(() => {
    const requiredDocuments = documentTypes.filter((doc) => doc.isRequired);
    setDocumentCount(requiredDocuments.length);
  }, [documentTypes]);

  const editCompanyInfo = (evt: any) => {
    setCompanyInfoOpen(true);
  };

  const uploadDocuments = (evt: any) => {
    setUploadDocumentsOpen(true);
  };

  const onboardDrivers = (evt: any) => {
    setOnboardDriversOpen(true);
  };

  const onboardVehicles = (evt: any) => {
    setOnboardVehiclesOpen(true);
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.content}>
        <Typography variant="h4" mb={2}>
          {t('onboard.onboarding')}
        </Typography>
        <Divider />
        <List>
          <ListItem>
            <ListItemText>
              <Typography variant="h5">
                {t('onboard.registerCompany')}
              </Typography>
              <Typography variant="h6">
                Started:{' '}
                {DateTime.fromISO(
                  userInfo?.onboardingUser?.createdOnUTC,
                ).toFormat('MMMM dd')}
              </Typography>
            </ListItemText>
            <Button
              variant="contained"
              color="primary"
              disabled={canCloseRegister}
              className={classes.button}
              onClick={editCompanyInfo}>
              {t(canCloseRegister ? 'onboard.done' : 'onboard.edit')}
            </Button>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText>
              <Typography variant="h5">
                {t('onboard.uploadDocuments')} ({documents.length} of{' '}
                {documentCount})
              </Typography>
              <Typography variant="h6">{t('onboard.documentStep')}</Typography>
            </ListItemText>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={
                !userInfo?.transportationProvider?.transportationProviderID
              }
              onClick={uploadDocuments}>
              {t('onboard.start')}
            </Button>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText>
              <Typography variant="h5">{t('onboard.addDrivers')}</Typography>
              <Typography variant="h6">{t('onboard.driverStep')}</Typography>
            </ListItemText>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={
                !userInfo?.transportationProvider?.transportationProviderID
              }
              onClick={onboardDrivers}>
              {t('onboard.start')}
            </Button>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText>
              <Typography variant="h5">{t('onboard.addVehicles')}</Typography>
              <Typography variant="h6">{t('onboard.vehicleStep')}</Typography>
            </ListItemText>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={
                !userInfo?.transportationProvider?.transportationProviderID
              }
              onClick={onboardVehicles}>
              {t('onboard.start')}
            </Button>
          </ListItem>
        </List>

        <Box width="100%" display="flex" justifyContent="center" mt={6}>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            disabled={
              !userInfo?.transportationProvider?.transportationProviderID
            }
            onClick={() => navigate(0)}>
            {t('onboard.submitOnboarding')}
          </Button>
        </Box>
      </Box>
      <RegisterProviderDialog
        canClose={canCloseRegister}
        open={companyInfoOpen}
        close={() => setCompanyInfoOpen(false)}
      />
      <OnboardDocumentsDialog
        open={uploadDocumentsOpen}
        close={() => setUploadDocumentsOpen(false)}
        uploadingDocument={uploadingDocument}
      />
      <OnboardDriversDialog
        open={onboardDriversOpen}
        close={() => setOnboardDriversOpen(false)}
      />
      <OnboardVehiclesDialog
        open={onboardVehiclesOpen}
        close={() => setOnboardVehiclesOpen(false)}
      />
    </Root>
  );
}
